import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../api/baseUrl";
import { addPrescription, updatePrescription } from "../../../redux/diagnosestate/diagnoseAction";
import {
  validatePrescriptionForm,
  type PrescriptionFormData,
} from "./prescriptionFormUtils";

type FormData = PrescriptionFormData;

interface PrescriptionEditItem {
  _id: string;
  drugsId?: string;
  drugs?: string;
  type?: string;
  dose?: string;
  doseId?: string;
  frequency?: string;
  duration?: string;
  createdAt?: string;
}

interface PrescriptionFormProps {
  treatmentId?: string;
  editItem?: PrescriptionEditItem | null;
  onCancelEdit?: () => void;
  onSuccess?: (item: any) => void;
  useRedux?: boolean;
  localOnly?: boolean;
  deferSave?: boolean;
  draftId?: string;
  onDraftChange?: (draftId: string, formData: PrescriptionFormData) => void;
  onDraftRemove?: (draftId: string) => void;
  inlineRow?: boolean;
}

function PrescriptionForm({
  treatmentId: treatmentIdProp,
  editItem,
  onCancelEdit,
  onSuccess,
  useRedux = true,
  localOnly = false,
  deferSave = false,
  draftId,
  onDraftChange,
  onDraftRemove,
  inlineRow = false,
}: PrescriptionFormProps) {
  const dispatch = useDispatch();
  const { ID } = useParams();
  const treatmentId = treatmentIdProp || ID;
  const initialFormData: FormData = {
    drugsId: "",
    drugsIds: "",
    drugs: "",
    dose: "",
    doseId: "",
    doseIds: "",
    frequency: "",
    duration: "",
    type: "",
  };
  const [formData, setformData] = useState(initialFormData);

  const [data, setData] = useState([]);

  useEffect(() => {
    baseUrl
      .get("/client/prescription/prescription/findAllPrescription")
      .then((response) => {
        setData(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to fetch diagnoses. Please try again.");
      });
  }, []);

  useEffect(() => {
    if (deferSave && draftId) return;
    if (!editItem) {
      setformData(initialFormData);
      return;
    }
    setformData({
      drugsId: editItem.drugsId || "",
      drugsIds: `${editItem.drugsId}/${editItem.drugs}/${editItem.type}`,
      drugs: editItem.drugs || "",
      dose: editItem.dose || editItem.doseId || "",
      doseId: editItem.doseId || editItem.dose || "",
      doseIds: `${editItem.doseId || editItem.dose}/${editItem.dose || editItem.doseId}`,
      frequency: editItem.frequency || "",
      duration: editItem.duration || "",
      type: editItem.type || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, deferSave, draftId]);

  useEffect(() => {
    if (deferSave && draftId && onDraftChange) {
      onDraftChange(draftId, formData);
    }
  }, [deferSave, draftId, formData, onDraftChange]);

  const diagnoseOptions = useMemo(
    () =>
      data.map((item: any) => (
        <option
          value={item._id + "/" + item.drugs + "/" + item.type}
          key={item._id}
        >
          {item.drugs}
        </option>
      )),
    [data]
  );
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "drugsIds") {
      setformData((prev) => ({
        ...prev,
        [name]: value,
        drugsId: value.split("/")[0],
        drugs: value.split("/")[1],
        type: value.split("/")[2],
      }));
    } else if (name === "doseIds") {
      setformData((prev) => ({
        ...prev,
        [name]: value,
        doseId: value.split("/")[0],
        dose: value.split("/")[1],
      }));
    } else {
      setformData((prev) => ({ ...prev, [name]: value }));
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const FREQUENCY_OPTIONS = [
    { value: "bd", label: "BD" },
    { value: "tds", label: "TDS" },
    { value: "qds", label: "QDS" },
    { value: "nocte", label: "Nocte" },
    { value: "sos", label: "SOS" },
    { value: "stat", label: "Stat" },
    { value: "stat , 100mg/nocte", label: "Stat, 100mg/nocte" },
    { value: "mane", label: "Mane" },
  ];

  const DURATION_OPTIONS = ["1", "2", "3", "4", "5", "7", "10", "14"];

  const compactSelectClass =
    "w-full min-w-0 px-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500";

  const handleSubmit = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const validation = validatePrescriptionForm(formData);
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }

      if (localOnly) {
        const normalizedItem = {
          ...formData,
          _id: editItem?._id || `local-${Date.now()}`,
          createdAt: editItem?.createdAt || new Date().toISOString(),
          drugs: formData.drugs,
          dose: formData.doseId,
        };
        onSuccess?.(normalizedItem);
        onCancelEdit?.();
        setformData(initialFormData);
        toast.success(editItem ? "Prescription updated." : "Prescription added.");
        return;
      }

      if (editItem?._id) {
        const response = await baseUrl.patch(
          "/client/treatments/prescription/updatePrescription",
          formData,
          {
            headers: {
              treatmentid: treatmentId,
              prescriptionid: editItem._id,
            },
          }
        );
        const updated = response.data.data;
        const normalizedItem = {
          ...formData,
          _id: editItem._id,
          createdAt: updated?.createdAt || editItem.createdAt,
          drugs: formData.drugs,
          dose: formData.doseId,
        };
        if (useRedux) {
          dispatch(updatePrescription(normalizedItem));
        }
        onSuccess?.(normalizedItem);
        onCancelEdit?.();
        setformData(initialFormData);
        toast.success("Prescription updated.");
        return;
      }

      const response = await baseUrl.patch(
        "/client/treatments/prescription/addprescription",
        formData,
        {
          headers: { treatmentid: treatmentId },
        }
      );

      const normalizedItem = {
        ...formData,
        createdAt: response.data.data.createdAt,
        _id: response.data.data._id,
        drugs: formData.drugs,
        dose: formData.doseId,
      };

      if (useRedux) {
        dispatch(addPrescription(normalizedItem));
      }
      onSuccess?.(normalizedItem);
      setformData(initialFormData);
    } catch (error) {
      console.error(error);
      toast.error(
        editItem ? "Failed to update prescription." : "Failed to submit the form."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRowSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    void handleSubmit();
  };

  const doseOptions = data
    ?.filter((item: any) => item._id === formData.drugsId)
    ?.map((filteredItem: any) => filteredItem.dose)
    ?.flat();

  const fields = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Drugs */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Drugs *</label>
          <select
            name="drugsIds"
            value={formData.drugsIds}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Drug</option>
            {diagnoseOptions}
          </select>
        </div>

        {/* Dose */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Dose *</label>
          <select
            name="doseIds"
            value={formData.doseIds}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Dose</option>
            {doseOptions?.map((item: any, i: number) => (
              <option key={i} value={item + "/" + item}>
                {item}
                {formData.type}
              </option>
            ))}
          </select>
        </div>

        {/* Frequency */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Frequency *</label>
          <select
            name="frequency"
            value={formData.frequency}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Frequency</option>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Duration (Days) *</label>
          <select
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Duration</option>
            {DURATION_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days} Day{days === "1" ? "" : "s"}
              </option>
            ))}
          </select>
        </div>
      </div>
  );

  if (inlineRow) {
    const isEdit = Boolean(editItem?._id);
    return (
      <tr className="bg-teal-50/60 dark:bg-teal-900/20 align-top">
        <td className="px-3 py-2" />
        <td className="px-3 py-2 min-w-[140px]">
          <select
            name="drugsIds"
            value={formData.drugsIds}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Drug *</option>
            {diagnoseOptions}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[100px]">
          <select
            name="doseIds"
            value={formData.doseIds}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Dose *</option>
            {doseOptions?.map((item: any, i: number) => (
              <option key={i} value={item + "/" + item}>
                {item}
                {formData.type}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[110px]">
          <select
            name="frequency"
            value={formData.frequency}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Freq *</option>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[90px]">
          <select
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Days *</option>
            {DURATION_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 text-xs text-slate-400">—</td>
        <td className="px-3 py-2">
          <div className="flex flex-col gap-1">
            {deferSave && !isEdit && (
              <span className="px-2 py-1 text-[10px] rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium whitespace-nowrap text-center">
                Draft
              </span>
            )}
            {isEdit && !deferSave && (
              <button
                type="button"
                onClick={handleRowSubmit}
                disabled={isSubmitting}
                className="px-2 py-1 text-[10px] rounded-md bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? "..." : "Update"}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                deferSave && draftId
                  ? onDraftRemove?.(draftId)
                  : onCancelEdit?.()
              }
              className="px-2 py-1 text-[10px] rounded-md border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 whitespace-nowrap"
            >
              {deferSave && !isEdit ? "Remove" : "Cancel"}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  if (deferSave && draftId && !inlineRow) {
    return (
      <div className="rounded-lg border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/40 dark:bg-teal-900/10 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
            Draft
          </span>
          <button
            type="button"
            onClick={() => onDraftRemove?.(draftId)}
            className="px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            Remove
          </button>
        </div>
        <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
          {fields}
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {fields}

      <div className="flex justify-end gap-2">
        {editItem && onCancelEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-teal-200 dark:hover:shadow-teal-900/30 transition-all disabled:opacity-50"
        >
          {isSubmitting
            ? editItem
              ? "Updating..."
              : "Adding..."
            : editItem
              ? "Update Prescription"
              : "Add Prescription"}
        </button>
      </div>
    </form>
  );
}

export default PrescriptionForm;
